/**
 * PrintPopup - Handles printing via QZ Tray in a popup window
 * 
 * This page runs on app.castlepos.com domain (not in GHL iframe),
 * so QZ Tray certificate validation works correctly.
 * 
 * Fetches print job data from server using job ID passed in URL.
 */
import { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

// QZ Tray embedded certificate for app.castlepos.com
const EMBEDDED_CERTIFICATE = `-----BEGIN CERTIFICATE-----
MIIEGzCCAwOgAwIBAgIURfvsl1RrLLn8mDQZlGCemoxV550wDQYJKoZIhvcNAQEL
BQAwgZwxCzAJBgNVBAYTAlVTMQ0wCwYDVQQIDARPaGlvMRQwEgYDVQQHDAtIb2xt
ZXN2aWxsZTETMBEGA1UECgwKQ2FzdGxlIFBPUzERMA8GA1UECwwIUHJpbnRpbmcx
GjAYBgNVBAMMEWFwcC5jYXN0bGVwb3MuY29tMSQwIgYJKoZIhvcNAQkBFhVzdXBw
b3J0QGNhc3RsZXBvcy5jb20wHhcNMjYwMTEzMTczMjAwWhcNMzYwMTExMTczMjAw
WjCBnDELMAkGA1UEBhMCVVMxDTALBgNVBAgMBE9oaW8xFDASBgNVBAcMC0hvbG1l
c3ZpbGxlMRMwEQYDVQQKDApDYXN0bGUgUE9TMREwDwYDVQQLDAhQcmludGluZzEa
MBgGA1UEAwwRYXBwLmNhc3RsZXBvcy5jb20xJDAiBgkqhkiG9w0BCQEWFXN1cHBv
cnRAY2FzdGxlcG9zLmNvbTCCASIwDQYJKoZIhvcNAQEBBQADggEPADCCAQoCggEB
AJVaEw50OBf9iqDgDDjgGnX8VVbfpZxU9a8X+kHElJRiYZz9eXxuoZE129fbdjiS
QStMKtWRcfQsl35mTdGjhDQPTwpANC6ps/GtzXJJrCgY84yLNXRRHpaVeZmpTnqF
0xOItMGyuJxlXO5wCPkfLtb0mhh0I6Qh+Xe9dtZSp2hFFSMdBJ6pTKARJlsf7khE
8s4rCcGlGqAZx8lxdmrvqEESCJfw3i50QtLbSPNdVNg3NzySan4LkZfM89cI77kH
NGRk9m0K7K+Ho4goHOlcVyO7DDRdtfrFnvdoqdKzB7do0b4n4FNCFQwvQHWCVEhU
fqrO9KSNDpy8MnU07BATWg8CAwEAAaNTMFEwHQYDVR0OBBYEFK5BCETZlfhPltkp
HdAzsC62zuAuMB8GA1UdIwQYMBaAFK5BCETZlfhPltkpHdAzsC62zuAuMA8GA1Ud
EwEB/wQFMAMBAf8wDQYJKoZIhvcNAQELBQADggEBAGnXdR7pomAPN0hak0mzQony
nIinkiyZmoiEXKQroL1W6uejI/Ka3iGqmJROvRQMXm0NlHuPjgyLyvFJoUTYBPit
Q2IIxeb3veaJJs/ILz/GaE/dMX6RXYHvta/1OnSqmLFODXxHENT5ilFAkqsbndkb
QDFTJc0NQbvGsLTehRpmI54CP+ROiXLvsnSe41J4QOfo70Z6FV7ZFbr5u09L1l1N
5dkXE54NkWkc4N7huMslVT1JhUggebJcd7t50GA0E/RHDeJOFaDYvoiVxb4+gnM+
VOpXn+8wxVERgS5H3g+yHK3udzlzEp8Szn68ujhMNX6e7rM8+rSiPMVFbwGVnx4=
-----END CERTIFICATE-----`;

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://app.castlepos.com';

export default function PrintPopup() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('initializing');
  const [message, setMessage] = useState('Loading print job...');
  const [error, setError] = useState(null);

  // Fetch print job from server
  const fetchPrintJob = useCallback(async (jobId) => {
    const response = await fetch(`${BACKEND_URL}/api/qz/print-job/${jobId}`);
    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Print job not found or expired. Please try again.');
      }
      throw new Error(`Failed to fetch print job: ${response.status}`);
    }
    return response.json();
  }, []);

  // Setup QZ Tray security
  const setupQZSecurity = useCallback(async (qz) => {
    console.log('[PrintPopup] Setting up QZ Tray security...');
    
    // IMPORTANT: Set signature algorithm to SHA512 to match backend signing
    // Must be called before setCertificatePromise and setSignaturePromise
    if (qz.security.setSignatureAlgorithm) {
      qz.security.setSignatureAlgorithm("SHA512");
      console.log('[PrintPopup] Signature algorithm set to SHA512');
    }
    
    // Set certificate promise
    qz.security.setCertificatePromise((resolve) => {
      console.log('[PrintPopup] Providing certificate for app.castlepos.com');
      resolve(EMBEDDED_CERTIFICATE);
    });

    // Set signature promise - get signature from backend
    qz.security.setSignaturePromise((toSign) => {
      return (resolve, reject) => {
        console.log('[PrintPopup] Requesting signature from backend...');
        fetch(`${BACKEND_URL}/api/qz/sign`, {
          method: 'POST',
          body: toSign,
          headers: { 'Content-Type': 'text/plain' }
        })
          .then(response => {
            if (!response.ok) throw new Error(`Signature failed: ${response.status}`);
            return response.text();
          })
          .then(signature => {
            console.log('[PrintPopup] Signature received');
            resolve(signature);
          })
          .catch(err => {
            console.error('[PrintPopup] Signature error:', err);
            reject(err);
          });
      };
    });
  }, []);

  // Connect to QZ Tray
  const connectToQZ = useCallback(async () => {
    if (typeof window.qz === 'undefined') {
      throw new Error('QZ Tray library not loaded. Please ensure QZ Tray is installed and running.');
    }

    const qz = window.qz;
    
    // Enable debug logging
    if (qz.api && qz.api.showDebug) {
      qz.api.showDebug(true);
      console.log('[PrintPopup] Debug mode enabled');
    }
    
    // Setup security first
    await setupQZSecurity(qz);
    
    // Check if already connected
    if (qz.websocket.isActive()) {
      console.log('[PrintPopup] Already connected to QZ Tray');
      return qz;
    }
    
    console.log('[PrintPopup] Connecting to QZ Tray with retries...');
    
    // Connect with retry options
    await qz.websocket.connect({ 
      retries: 3, 
      delay: 1 
    });
    
    console.log('[PrintPopup] Connected to QZ Tray successfully');
    
    return qz;
  }, [setupQZSecurity]);

  // Clean up print job on server after use
  const cleanupJob = useCallback(async (jobId) => {
    try {
      await fetch(`${BACKEND_URL}/api/qz/print-job/${jobId}`, { method: 'DELETE' });
    } catch (e) {
      // Non-critical, job expires on its own
    }
  }, []);

  // Render HTML to a base64 PNG image using html2canvas in the browser
  const renderHTMLToImage = useCallback(async (html, margins) => {
    const { default: html2canvas } = await import('html2canvas');
    
    // Create a hidden container to render the receipt
    const container = document.createElement('div');
    // Apply CSS padding based on margins (Settings provides these in mm)
    const leftMargin = margins?.left || 0;
    const rightMargin = margins?.right || 0;
    
    // We adjust box-sizing so width 302px includes the padding.
    // This squishes the inner HTML content just like physical margins would.
    container.style.cssText = `
      position:fixed; left:-9999px; top:0; 
      width:302px; background:white; 
      box-sizing:border-box;
      padding-left:${leftMargin}mm; 
      padding-right:${rightMargin}mm;
    `;
    container.innerHTML = html;
    document.body.appendChild(container);
    
    console.log('[PrintPopup] Rendering HTML to image...');
    
    try {
      const canvas = await html2canvas(container, {
        scale: 2, // High quality
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        width: 302,
        windowWidth: 302,
        logging: false,
      });
      
      // Convert to base64 PNG
      const base64 = canvas.toDataURL('image/png').replace(/^data:image\/png;base64,/, '');
      console.log('[PrintPopup] Image rendered, size:', base64.length, 'chars');
      return base64;
    } finally {
      document.body.removeChild(container);
    }
  }, []);

  // Print receipt by rendering HTML to image client-side, then sending image to QZ Tray
  // This bypasses QZ Tray's broken Java HTML renderer entirely
  const printHTML = useCallback(async (qz, html, printerName, margins = {}) => {
    console.log('[PrintPopup] Printing to:', printerName || 'default printer', 'Margins:', margins);
    
    let printer = printerName;
    if (!printer) {
      const printers = await qz.printers.find();
      if (printers.length === 0) {
        throw new Error('No printers found');
      }
      printer = printers[0];
      console.log('[PrintPopup] Using default printer:', printer);
    }
    
    // Render HTML to image in the browser
    const base64Image = await renderHTMLToImage(html, margins);
    
    console.log('[PrintPopup] Creating config for printer:', printer);
    const config = qz.configs.create(printer, {
      size: { width: 3.15, height: 0 }, // 80mm width, auto height
      units: 'in',
      scaleContent: true,
      interpolation: 'bicubic',
      jobName: 'Castle POS Receipt',
      margins: { top: 0, right: 0, bottom: 0, left: 0 }
    });
    
    // Send the pre-rendered image directly — no Java HTML rendering needed
    const data = [{
      type: 'pixel',
      format: 'image',
      data: 'data:image/png;base64,' + base64Image
    }];

    console.log('[PrintPopup] Calling qz.print() with pre-rendered image...');
    
    // Add timeout wrapper to detect hanging
    const printPromise = qz.print(config, data);
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Print timeout after 60 seconds')), 60000);
    });
    
    try {
      await Promise.race([printPromise, timeoutPromise]);
      console.log('[PrintPopup] Print job sent successfully');
    } catch (err) {
      console.error('[PrintPopup] Print failed:', err.message);
      throw err;
    }
  }, [renderHTMLToImage]);

  // Open cash drawer
  const openDrawer = useCallback(async (qz, printerName) => {
    console.log('[PrintPopup] Opening cash drawer via:', printerName || 'default printer');
    
    let printer = printerName;
    if (!printer) {
      const printers = await qz.printers.find();
      if (printers.length === 0) {
        throw new Error('No printers found');
      }
      printer = printers[0];
    }
    
    const config = qz.configs.create(printer);
    
    // Standard ESC/POS cash drawer commands - try multiple variants
    // ESC p m t1 t2 - where m=0 or 1 for pin 2 or 5, t1/t2 are pulse timings
    // Most common: ESC p 0 25 250 (pin 2) or ESC p 1 25 250 (pin 5)
    const drawerCommands = [
      '\x1B\x70\x00\x19\xFA',  // ESC p 0 25 250 - Pin 2 (most common)
      '\x1B\x70\x01\x19\xFA',  // ESC p 1 25 250 - Pin 5 (alternative)
    ];
    
    // Use the first command (most common for Epson-compatible)
    const data = [{
      type: 'raw',
      format: 'command',
      data: drawerCommands[0],
      options: { language: 'ESCPOS' }
    }];

    console.log('[PrintPopup] Sending cash drawer kick command...');
    await qz.print(config, data);
    console.log('[PrintPopup] Cash drawer command sent');
  }, []);

  // Main execution
  useEffect(() => {
    const execute = async () => {
      try {
        // Get job ID from URL
        const jobId = searchParams.get('jobId');
        
        if (!jobId) {
          throw new Error('No job ID provided');
        }

        console.log('[PrintPopup] Fetching job:', jobId);
        setMessage('Loading print job...');
        
        // Fetch print job data from server
        const jobData = await fetchPrintJob(jobId);
        const { action, html, printer: printerName, margins = {} } = jobData;
        
        console.log('[PrintPopup] Job data received:');
        console.log('[PrintPopup]   - action:', action);
        console.log('[PrintPopup]   - printer:', printerName);
        console.log('[PrintPopup]   - margins:', margins);
        console.log('[PrintPopup]   - hasHtml:', !!html);
        console.log('[PrintPopup]   - htmlLength:', html?.length);
        if (html) {
          console.log('[PrintPopup] HTML preview (first 200 chars):', html.substring(0, 200));
        }

        // Connect to QZ Tray
        setMessage('Connecting to QZ Tray...');
        const qz = await connectToQZ();
        
        // Handle different actions
        if (action === 'print') {
          setMessage('Printing receipt...');
          console.log('[PrintPopup] Starting print action...');
          
          // Small delay to ensure popup is fully ready
          await new Promise(resolve => setTimeout(resolve, 500));
          
          await printHTML(qz, html, printerName, margins);
          console.log('[PrintPopup] Print action completed');
          
          setStatus('success');
          setMessage('Receipt printed successfully!');
          
        } else if (action === 'drawer') {
          setMessage('Opening cash drawer...');
          await openDrawer(qz, printerName);
          
          setStatus('success');
          setMessage('Cash drawer opened!');
          
        } else if (action === 'printAndDrawer') {
          setMessage('Printing receipt...');
          await printHTML(qz, html, printerName, margins);
          
          setMessage('Opening cash drawer...');
          await openDrawer(qz, printerName);
          
          setStatus('success');
          setMessage('Receipt printed and drawer opened!');
          
        } else if (action === 'listPrinters') {
          setMessage('Getting printer list...');
          const printers = await qz.printers.find();
          console.log('[PrintPopup] Found printers:', printers);
          
          // Store printers on server for cross-origin retrieval
          const cacheKey = jobData.cacheKey || 'default';
          try {
            await fetch(`${BACKEND_URL}/api/qz/printers`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ printers, cache_key: cacheKey })
            });
            console.log('[PrintPopup] Stored printers on server');
          } catch (err) {
            console.error('[PrintPopup] Failed to store printers:', err);
          }
          
          // Also send via postMessage
          if (window.opener) {
            window.opener.postMessage({ 
              type: 'QZ_PRINTERS_FOUND', 
              printers 
            }, '*');
          }
          
          setStatus('success');
          setMessage(`Found ${printers.length} printer(s)`);
          
        } else if (action === 'test') {
          setMessage('Sending test print...');
          
          const testHTML = `
            <html>
              <head>
                <style>
                  body { font-family: monospace; text-align: center; margin: 0; padding: 10px; width: 280px; }
                  h1 { font-size: 20px; margin: 10px 0; }
                  p { font-size: 12px; margin: 5px 0; }
                  .line { border-top: 1px dashed #000; margin: 10px 0; }
                  .check { color: green; font-size: 16px; }
                </style>
              </head>
              <body>
                <h1>TEST PRINT</h1>
                <p><strong>Castle POS</strong></p>
                <div class="line"></div>
                <p>Printer: ${printerName || 'Default'}</p>
                <p>Time: ${new Date().toLocaleString()}</p>
                <div class="line"></div>
                <p class="check">✓ QZ Tray Connected</p>
                <p class="check">✓ Certificate Valid</p>
                <p class="check">✓ Printing Works!</p>
                <div class="line"></div>
              </body>
            </html>
          `;
          
          await printHTML(qz, testHTML, printerName);
          
          setStatus('success');
          setMessage('Test print sent successfully!');
        } else {
          throw new Error(`Unknown action: ${action}`);
        }

        // Notify parent window of success
        if (window.opener) {
          window.opener.postMessage({ type: 'QZ_PRINT_SUCCESS', action }, '*');
        }

        // Auto-close after short delay
        setTimeout(() => {
          window.close();
        }, 2000);

      } catch (err) {
        console.error('[PrintPopup] Error:', err);
        setStatus('error');
        setError(err.message);
        setMessage('Print failed');

        // Notify parent window of error
        if (window.opener) {
          window.opener.postMessage({ type: 'QZ_PRINT_ERROR', error: err.message }, '*');
        }
      }
    };

    // Small delay to ensure QZ library is loaded
    setTimeout(execute, 300);
  }, [searchParams, fetchPrintJob, connectToQZ, printHTML, openDrawer, cleanupJob]);

  return (
    <div style={{ 
      minHeight: '100vh', 
      backgroundColor: '#f3f4f6', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center',
      padding: '16px',
      fontFamily: 'system-ui, -apple-system, sans-serif'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
        padding: '32px',
        maxWidth: '400px',
        width: '100%',
        textAlign: 'center'
      }}>
        <h1 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '16px', color: '#1f2937' }}>
          Castle POS Print
        </h1>
        
        {status === 'initializing' && (
          <div>
            <div style={{
              width: '48px',
              height: '48px',
              border: '3px solid #e5e7eb',
              borderTop: '3px solid #3b82f6',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 16px'
            }} />
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            <p style={{ color: '#6b7280' }}>{message}</p>
          </div>
        )}
        
        {status === 'success' && (
          <div>
            <div style={{ fontSize: '48px', color: '#22c55e', marginBottom: '16px' }}>✓</div>
            <p style={{ color: '#22c55e', fontWeight: '600', marginBottom: '8px' }}>{message}</p>
            <p style={{ color: '#9ca3af', fontSize: '14px' }}>This window will close automatically...</p>
          </div>
        )}
        
        {status === 'error' && (
          <div>
            <div style={{ fontSize: '48px', color: '#ef4444', marginBottom: '16px' }}>✕</div>
            <p style={{ color: '#ef4444', fontWeight: '600', marginBottom: '8px' }}>{message}</p>
            <p style={{ color: '#ef4444', fontSize: '14px', marginBottom: '16px', wordBreak: 'break-word' }}>{error}</p>
            <button
              onClick={() => window.close()}
              style={{
                padding: '8px 24px',
                backgroundColor: '#e5e7eb',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
